import {useCallback, useEffect, useState} from 'react';
import {Spinner} from 'flowbite-react';
import {HiOutlineRefresh, HiOutlineChatAlt2} from 'react-icons/hi';
import {useAppContext} from "../../../helpers/hooks/common-hook";
import {getSession, SESSION_PAGE_SIZE} from "../../../services/session";
import {fromNow} from "../../../helpers/utils/date-util";
import { safeString } from '../../../helpers/utils/text-util';
import SessionPager from './session-pager';

export default function SessionsList({appId: envAppId, onSelectSession, selectedSessionId}) {
    const { appInfo } = useAppContext();
    // The environment's id (`<appId>` or `<appId>_live`) when the page passes one.
    const appId = envAppId || appInfo.appId;
    const [sessions, setSessions] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [pageSize, setPageSize] = useState(SESSION_PAGE_SIZE);

    // Cursors of the pages already visited, oldest first. The backend cursor
    // only moves forward, so stepping back means re-requesting with the cursor
    // that produced the previous page - index 0 is the first page (no cursor).
    const [cursorStack, setCursorStack] = useState([null]);

    const [fetchSessionsState, setFetchSessionsState] = useState({
        processing: false,
        success: false,
        failed: false,
        errorMessage: ''
    });

    const updateFetchSessionsState = (processing, success, failed, errorMessage) => {
        setFetchSessionsState(Object.assign({}, { processing, success, failed, errorMessage }))
    }

    /**
     * Load one page of sessions.
     *
     * `cursors` is the new cursor stack: its last entry is the cursor for the
     * page being requested, and its length is the 1-based page number.
     */
    const fetchPage = useCallback(async (cursors, size) => {
        if (!appId) {
            return;
        }

        updateFetchSessionsState(true, false);
        const requestPage = cursors[cursors.length - 1];
        const response = await getSession(appId, requestPage || undefined, size);

        setSessions(response.data || []);
        setNextPage(response.pagination?.nextPage || null);
        setCursorStack(cursors);
        updateFetchSessionsState(false, true);
    }, [appId]);

    // Reset to the first page whenever the app changes. fetchPage is keyed on
    // appId, so this also covers the initial load.
    useEffect(() => {
        fetchPage([null], pageSize);
        // pageSize is intentionally not a dependency: changing it already
        // refetches through changePageSize, and listing it here would fire a
        // second, duplicate request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchPage]);

    // Refresh reloads the page currently on screen, keeping the user in place.
    const refresh = useCallback(() => {
        return fetchPage(cursorStack, pageSize);
    }, [fetchPage, cursorStack, pageSize]);

    const goToNextPage = useCallback(() => {
        if (!nextPage || fetchSessionsState.processing) {
            return;
        }
        fetchPage([...cursorStack, nextPage], pageSize);
    }, [fetchPage, cursorStack, nextPage, fetchSessionsState.processing, pageSize]);

    const goToPreviousPage = useCallback(() => {
        if (cursorStack.length <= 1 || fetchSessionsState.processing) {
            return;
        }
        fetchPage(cursorStack.slice(0, -1), pageSize);
    }, [fetchPage, cursorStack, fetchSessionsState.processing, pageSize]);

    // A different page size invalidates every cursor, so restart from page one.
    const changePageSize = useCallback(size => {
        if (size === pageSize) {
            return;
        }
        setPageSize(size);
        fetchPage([null], size);
    }, [fetchPage, pageSize]);

    // There is no total count from a DynamoDB cursor, so the pager shows the row
    // range of the current page rather than "x of y". Pages before this one were
    // full by construction, which makes the offset exact.
    const rangeStart = sessions.length === 0
        ? 0
        : (cursorStack.length - 1) * pageSize + 1;
    const rangeEnd = sessions.length === 0
        ? 0
        : rangeStart + sessions.length - 1;

    return (
        <div className='flex h-full min-h-0 flex-col border-r border-gray-200 bg-white'>

            <div className='flex h-12 shrink-0 items-center justify-between border-b border-gray-200 px-3'>
                <h2 className='text-sm font-semibold text-gray-900'>
                    Sessions
                    {sessions.length > 0 &&
                        <span className='ml-1.5 text-xs font-normal text-gray-400'>
                            {sessions.length}
                        </span>
                    }
                </h2>

                <button
                    type='button'
                    onClick={refresh}
                    title='Refresh sessions'
                    className='rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700'
                >
                    <HiOutlineRefresh size={15} className={fetchSessionsState.processing ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto'>
                {fetchSessionsState.processing && sessions.length === 0 &&
                    <div className='flex h-full items-center justify-center'>
                        <Spinner size='md' />
                    </div>
                }

                {sessions.length > 0 &&
                    <ul className="divide-y divide-gray-100">
                        {sessions.map((session) => {
                            const selected = session.sessionId === selectedSessionId;
                            return (
                                <li key={session.sessionId || session.refId}>
                                    {/* A button, not an anchor: this selects a
                                        session rather than navigating, and the
                                        old href="#" put a stray entry in history
                                        and jumped the page on every click. */}
                                    <button
                                        type='button'
                                        onClick={() => onSelectSession(session)}
                                        className={`w-full border-l-2 px-3 py-2.5 text-left transition-colors ${selected
                                            ? 'border-indigo-600 bg-indigo-50'
                                            : 'border-transparent hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex items-baseline justify-between gap-2">
                                            <p className={`truncate text-xs font-semibold ${selected ? 'text-indigo-700' : 'text-gray-900'}`}>
                                                {session.refId}
                                            </p>
                                            <time className="shrink-0 text-[10px] text-gray-400">
                                                {fromNow(session.modifiedAt)}
                                            </time>
                                        </div>
                                        <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-gray-500">
                                            {safeString(session.displayMessage)}
                                        </p>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                }

                {!fetchSessionsState.processing && sessions.length === 0 &&
                    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                        <HiOutlineChatAlt2 size={22} className='text-gray-300' />
                        <p className="mt-2 text-xs font-medium text-gray-900">
                            {cursorStack.length > 1 ? 'No sessions on this page' : 'No sessions yet'}
                        </p>
                        <p className="mt-0.5 text-[11px] text-gray-500">
                            {cursorStack.length > 1
                                ? 'Go back to the previous page.'
                                : 'Conversations appear here once someone starts chatting.'}
                        </p>
                    </div>
                }
            </div>

            <SessionPager
                pageNumber={cursorStack.length}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                pageSize={pageSize}
                onPageSizeChange={changePageSize}
                hasPrevious={cursorStack.length > 1}
                hasNext={Boolean(nextPage)}
                onPrevious={goToPreviousPage}
                onNext={goToNextPage}
                loading={fetchSessionsState.processing}
            />

        </div>
    )
}
